import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Tooltip,
  Drawer
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Functions as FormulaIcon,
  DataObject as DataIcon,
  Person as UserIcon,
  History as PreviousIcon,
  Edit as CustomIcon
} from '@mui/icons-material';

import {
  AutomaticStateType,
  ParameterBinding,
  ParameterDataSource,
  STATE_TYPE_DEFINITIONS,
  WorkflowContext,
  AutomaticStateConfig
} from './types';
import PreviousOutputSelector from './PreviousOutputSelector';

interface ParameterConfigurationProps {
  currentNodeId: string;
  moduleId?: number;
  stateType: AutomaticStateType;
  config: AutomaticStateConfig;
  workflowContext: WorkflowContext;
  onChange: (config: AutomaticStateConfig) => void;
  onClose?: () => void;
}

const DATA_SOURCE_ICONS = {
  [ParameterDataSource.MODULE_DATA]: DataIcon,
  [ParameterDataSource.PREVIOUS_OUTPUTS]: PreviousIcon,
  [ParameterDataSource.USER_INFO]: UserIcon,
  [ParameterDataSource.CUSTOM_INPUT]: CustomIcon,
  [ParameterDataSource.FORMULA]: FormulaIcon,
};

const ParameterConfiguration: React.FC<ParameterConfigurationProps> = ({
  currentNodeId,
  moduleId,
  stateType,
  config,
  workflowContext,
  onChange,
  onClose
}) => {
  const [localConfig, setLocalConfig] = useState<AutomaticStateConfig>(config);
  const definition = STATE_TYPE_DEFINITIONS[stateType];

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleParameterChange = (parameterId: string, binding: Partial<ParameterBinding>) => {
    const updatedParameters = localConfig.parameters.map(param =>
      param.parameterId === parameterId ? { ...param, ...binding } : param
    );

    // Add parameter if it doesn't exist
    if (!updatedParameters.find(p => p.parameterId === parameterId)) {
      updatedParameters.push({
        parameterId,
        dataSource: ParameterDataSource.CUSTOM_INPUT,
        ...binding
      } as ParameterBinding);
    }

    const updatedConfig = {
      ...localConfig,
      parameters: updatedParameters
    };

    setLocalConfig(updatedConfig);
    // Don't call onChange here - only on explicit save
  };

  const getParameterBinding = (parameterId: string): ParameterBinding | undefined => {
    return localConfig.parameters.find(p => p.parameterId === parameterId);
  };

  const getDataSourceOptions = (dataSource: ParameterDataSource) => {
    switch (dataSource) {
      case ParameterDataSource.MODULE_DATA:
        return workflowContext.moduleData.fields.map(field => ({
          value: field.name,
          label: field.displayName,
          type: field.type
        }));
      
      case ParameterDataSource.PREVIOUS_OUTPUTS:
        return Object.keys(workflowContext.previousOutputs).map(stateId => ({
          value: stateId,
          label: `Output from State ${stateId}`,
          type: 'object'
        }));
      
      case ParameterDataSource.USER_INFO:
        return [
          { value: 'id', label: 'User ID', type: 'string' },
          { value: 'name', label: 'Full Name', type: 'string' },
          { value: 'email', label: 'Email Address', type: 'string' },
          { value: 'department', label: 'Department', type: 'string' },
          { value: 'roles', label: 'User Roles', type: 'array' }
        ];
      
      default:
        return [];
    }
  };

  const renderParameterEditor = (parameter: any) => {
    const binding = getParameterBinding(parameter.id);
    const IconComponent = DATA_SOURCE_ICONS[binding?.dataSource || ParameterDataSource.CUSTOM_INPUT];

    return (
      <Accordion key={parameter.id} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <IconComponent fontSize="small" color="primary" />
            <Typography variant="subtitle2" flex={1}>
              {intl.get(`automaticActions.${stateType}.param.${parameter.id}.name`)}
            </Typography>
            {parameter.required && (
              <Chip label={intl.get('parameterConfiguration.required')} size="small" color="error" variant="outlined" />
            )}
            {binding && (
              <Chip 
                label={binding.dataSource.replace('_', ' ')} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              {intl.get(`automaticActions.${stateType}.param.${parameter.id}.description`)}
            </Typography>

            {/* Data Source Selection */}
            <FormControl fullWidth sx={{ overflow: 'visible' }}>
              <InputLabel shrink>{intl.get('parameterConfiguration.dataSource')}</InputLabel>
              <Select
                notched
                label={intl.get('parameterConfiguration.dataSource')}
                value={binding?.dataSource || ParameterDataSource.CUSTOM_INPUT}
                onChange={(e) => {
                  const newDataSource = e.target.value as ParameterDataSource;
                  handleParameterChange(parameter.id, {
                    dataSource: newDataSource,
                    // Only clear values when switching away from current source
                    ...(newDataSource !== ParameterDataSource.CUSTOM_INPUT && { customValue: undefined }),
                    ...(newDataSource !== ParameterDataSource.FORMULA && { formula: undefined }),
                    ...((newDataSource !== ParameterDataSource.MODULE_DATA && 
                         newDataSource !== ParameterDataSource.PREVIOUS_OUTPUTS && 
                         newDataSource !== ParameterDataSource.USER_INFO) && { sourcePath: undefined })
                  });
                }}
              >
                <MenuItem value={ParameterDataSource.CUSTOM_INPUT}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CustomIcon fontSize="small" />
                    {intl.get('parameterConfiguration.customInput')}
                  </Box>
                </MenuItem>
                <MenuItem value={ParameterDataSource.MODULE_DATA}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DataIcon fontSize="small" />
                    {intl.get('parameterConfiguration.moduleData')}
                  </Box>
                </MenuItem>
                <MenuItem value={ParameterDataSource.PREVIOUS_OUTPUTS}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PreviousIcon fontSize="small" />
                    {intl.get('parameterConfiguration.previousOutputs')}
                  </Box>
                </MenuItem>
                <MenuItem value={ParameterDataSource.USER_INFO}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <UserIcon fontSize="small" />
                    {intl.get('parameterConfiguration.userInformation')}
                  </Box>
                </MenuItem>
                <MenuItem value={ParameterDataSource.FORMULA}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormulaIcon fontSize="small" />
                    {intl.get('parameterConfiguration.formula')}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Data Source Specific Inputs */}
            {(binding?.dataSource === ParameterDataSource.CUSTOM_INPUT || !binding?.dataSource) && (
              <TextField
                fullWidth
                label={intl.get('parameterConfiguration.customValue')}
                value={binding?.customValue || ''}
                onChange={(e) => handleParameterChange(parameter.id, {
                  dataSource: ParameterDataSource.CUSTOM_INPUT,
                  customValue: e.target.value
                })}
                type={parameter.type === 'number' ? 'number' : 'text'}
                multiline={parameter.type === 'string' && parameter.id.includes('body')}
                rows={parameter.id.includes('body') ? 4 : 1}
              />
            )}

            {binding?.dataSource === ParameterDataSource.PREVIOUS_OUTPUTS && (
              <PreviousOutputSelector
                currentNodeId={currentNodeId}
                moduleId={moduleId}
                selectedNodeId={binding?.sourcePath?.split('.')[0] || null}
                selectedFieldName={binding?.sourcePath?.split('.')[1] || null}
                onNodeChange={(nodeId) => {
                  // When node changes, reset the field
                  handleParameterChange(parameter.id, {
                    sourcePath: nodeId ? `${nodeId}.` : undefined
                  });
                }}
                onFieldChange={(fieldName) => {
                  const nodeId = binding?.sourcePath?.split('.')[0];
                  if (nodeId && fieldName) {
                    handleParameterChange(parameter.id, {
                      sourcePath: `${nodeId}.${fieldName}`
                    });
                  }
                }}
                onPathChange={(path) => {
                  // Optional: handle path changes if needed
                  console.log('Previous output path:', path);
                }}
              />
            )}

            {(binding?.dataSource === ParameterDataSource.MODULE_DATA ||
              binding?.dataSource === ParameterDataSource.USER_INFO) && (
              <FormControl fullWidth sx={{ overflow: 'visible' }}>
                <InputLabel shrink>{intl.get('parameterConfiguration.selectField')}</InputLabel>
                <Select
                  notched
                  label={intl.get('parameterConfiguration.selectField')}
                  value={binding?.sourcePath || ''}
                  onChange={(e) => handleParameterChange(parameter.id, {
                    sourcePath: e.target.value
                  })}
                >
                  {getDataSourceOptions(binding.dataSource).map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.type}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {binding?.dataSource === ParameterDataSource.FORMULA && (
              <TextField
                fullWidth
                label={intl.get('parameterConfiguration.formulaExpression')}
                value={binding?.formula || ''}
                onChange={(e) => handleParameterChange(parameter.id, {
                  formula: e.target.value
                })}
                placeholder={intl.get('parameterConfiguration.formulaPlaceholder')}
                helperText={intl.get('parameterConfiguration.formulaHelper')}
                multiline
                rows={3}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const handleSave = () => {
    onChange(localConfig);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={!!stateType}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 500,
          maxWidth: '100%'
        }
      }}
    >
      <Box p={3} sx={{ height: '100%', overflow: 'auto' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            {intl.get('parameterConfiguration.configure', { name: intl.get(`automaticActions.${stateType}.name`) })}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Typography variant="h6">✕</Typography>
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {intl.get(`automaticActions.${stateType}.description`)}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          {intl.get('parameterConfiguration.parameters', { count: definition.parameters.length })}
        </Typography>

        <Box display="flex" flexDirection="column" gap={1}>
          {definition.parameters.map(renderParameterEditor)}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Retry Configuration */}
        <Typography variant="subtitle1" gutterBottom>
          {intl.get('parameterConfiguration.retryConfiguration')}
        </Typography>
        
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            label={intl.get('parameterConfiguration.maxRetries')}
            type="number"
            value={localConfig.retryConfig?.maxRetries || 3}
            onChange={(e) => setLocalConfig({
              ...localConfig,
              retryConfig: {
                ...localConfig.retryConfig,
                maxRetries: parseInt(e.target.value) || 3
              }
            })}
            inputProps={{ min: 0, max: 10 }}
          />
          
          <TextField
            fullWidth
            label={intl.get('parameterConfiguration.retryDelay')}
            type="number"
            value={localConfig.retryConfig?.retryDelay || 5}
            onChange={(e) => setLocalConfig({
              ...localConfig,
              retryConfig: {
                ...localConfig.retryConfig,
                retryDelay: parseInt(e.target.value) || 5
              }
            })}
            inputProps={{ min: 1, max: 300 }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" mt={3} gap={2}>
          <Button variant="outlined" onClick={onClose} fullWidth>
            {intl.get('parameterConfiguration.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} fullWidth>
            {intl.get('parameterConfiguration.saveConfiguration')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ParameterConfiguration;
